import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage, formatPhone } from '@/lib/utils';

const INV_OPTIONS = ['not_invited', 'invited', 'accepted', 'declined'];
const REG_OPTIONS = ['not_registered', 'registered', 'cancelled'];
const RSVP_OPTIONS = ['going', 'maybe', 'declined'];
const ATT_OPTIONS = ['pending', 'attended', 'no_show', 'walk_in'];

const asDate = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * Compact right-side drawer to view + edit a single participant row.
 * Manager-only mutations flow through PATCH /participants/{aid}/manage.
 */
export default function ParticipantDetailDrawer({
  open, event, row, users, currentUser, canManage, onClose, onChanged,
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => rowToForm(row));

  React.useEffect(() => { setForm(rowToForm(row)); }, [row]);

  if (!row || !event) return null;

  const u = users?.[row.user_id] || null;
  const displayName = u?.name || u?.email || [row.guest_first_name, row.guest_last_name].filter(Boolean).join(' ') || '—';
  const email = u?.email || row.guest_email || '—';
  const phone = formatPhone(u?.phone || row.guest_phone) || '—';

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const patch = {};
      if (form.invitation_status !== row.invitation_status) patch.invitation_status = form.invitation_status;
      if (form.registration_status !== row.registration_status) patch.registration_status = form.registration_status;
      if (form.rsvp_response !== (row.rsvp_response || '')) patch.rsvp_response = form.rsvp_response || null;
      if (form.attendance_status !== row.attendance_status) patch.attendance_status = form.attendance_status;
      const gc = Number(form.guest_count) || 0;
      if (gc !== (row.guest_count || 0)) patch.guest_count = gc;
      const gnames = form.guest_names_text
        .split(',').map((s) => s.trim()).filter(Boolean);
      const rowNames = row.guest_names || [];
      if (JSON.stringify(gnames) !== JSON.stringify(rowNames)) patch.guest_names = gnames;
      if ((form.notes || '') !== (row.notes || '')) patch.notes = form.notes || null;
      if (Object.keys(patch).length === 0) {
        toast('No changes to save');
        return;
      }
      await calendarAPI.manageParticipant(event.event_id, row.assignment_id, patch);
      toast.success('Participant updated');
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="participant-detail-drawer">
        <SheetHeader>
          <SheetTitle>{displayName}</SheetTitle>
          <SheetDescription className="text-xs">
            Participant details {canManage ? '· editing enabled' : '(read-only)'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          <Section title="Contact">
            <KV label="Email" value={email} />
            <KV label="Phone" value={phone} />
            <KV label="Agency" value={row.guest_agency || '—'} />
            <KV label="NPN" value={row.guest_npn || '—'} />
          </Section>

          <Section title="Event Status">
            {canManage ? (
              <>
                <SelectField label="Invitation" value={form.invitation_status || ''} options={INV_OPTIONS} onChange={(v) => setField('invitation_status', v)} testId="pd-invitation-status" />
                <SelectField label="Registration" value={form.registration_status || ''} options={REG_OPTIONS} onChange={(v) => setField('registration_status', v)} testId="pd-registration-status" />
                <SelectField label="RSVP" value={form.rsvp_response || ''} options={['', ...RSVP_OPTIONS]} onChange={(v) => setField('rsvp_response', v)} testId="pd-rsvp-response" />
                <SelectField label="Attendance" value={form.attendance_status || ''} options={ATT_OPTIONS} onChange={(v) => setField('attendance_status', v)} testId="pd-attendance-status" />
              </>
            ) : (
              <>
                <KV label="Invitation" value={<Badge variant="outline" className="capitalize">{row.invitation_status || '—'}</Badge>} />
                <KV label="Registration" value={<Badge variant="outline" className="capitalize">{row.registration_status || '—'}</Badge>} />
                <KV label="RSVP" value={<Badge variant="outline" className="capitalize">{row.rsvp_response || '—'}</Badge>} />
                <KV label="Attendance" value={<Badge variant="outline" className="capitalize">{row.attendance_status || '—'}</Badge>} />
              </>
            )}
          </Section>

          <Section title="Registration">
            <KV label="Source" value={<span className="capitalize">{(row.registration_source || 'unknown').replace(/_/g, ' ')}</span>} />
            <KV label="Registered by" value={row.registered_by_name || <span className="text-slate-400">Unknown</span>} />
            <KV label="Registered at" value={asDate(row.registered_at || row.assigned_at)} />
            <KV label="RSVP at" value={asDate(row.rsvp_at)} />

            {canManage ? (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Guest count</Label>
                  <Input type="number" min={0} value={form.guest_count} onChange={(e) => setField('guest_count', e.target.value)} data-testid="pd-guest-count" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Guest names (comma-separated)</Label>
                  <Input value={form.guest_names_text} onChange={(e) => setField('guest_names_text', e.target.value)} data-testid="pd-guest-names" />
                </div>
              </>
            ) : (
              <>
                <KV label="Guests" value={row.guest_count || 0} />
                {(row.guest_names || []).length > 0 && (
                  <KV label="Guest names" value={(row.guest_names || []).join(', ')} />
                )}
              </>
            )}
          </Section>

          {canManage && (
            <Section title="Internal notes">
              <Textarea
                rows={3}
                value={form.notes || ''}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Notes visible only to managers…"
                data-testid="pd-notes"
              />
            </Section>
          )}
        </div>

        <div className="sticky bottom-0 -mx-6 px-6 py-3 mt-6 bg-white border-t border-slate-200 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="pd-close">Close</Button>
          {canManage && (
            <Button onClick={save} disabled={saving} data-testid="pd-save">
              Save changes
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function rowToForm(row) {
  if (!row) return {};
  return {
    invitation_status: row.invitation_status || '',
    registration_status: row.registration_status || '',
    rsvp_response: row.rsvp_response || '',
    attendance_status: row.attendance_status || '',
    guest_count: row.guest_count || 0,
    guest_names_text: (row.guest_names || []).join(', '),
    notes: row.notes || '',
  };
}

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
    <div className="space-y-3">{children}</div>
  </section>
);

const KV = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-2 text-sm">
    <div className="text-slate-500 text-xs">{label}</div>
    <div className="col-span-2 text-slate-800 break-words">{value}</div>
  </div>
);

const SelectField = ({ label, value, options, onChange, testId }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o || '__none__'} value={o || '__none__'} className="capitalize">
            {o ? o.replace(/_/g, ' ') : '—'}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
